import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import { Action, ActionMeta } from 'redux-actions';
import { Channel } from 'redux-saga';

import {
	ADD_SERVICE,
	REMOVE_SERVICE,
	SAVE_SERVICES,
	SET_SERVICE,
	DELETE_ALL_TYPES_ON_SEGMENT,
	SET_MULTIPLE_SELECTED_SERVICES,
	ServicePayload,
	ServiceMeta
} from './actions';
import { createClient } from '../../../graphql-client';
import { getSelectedServices, getSelectedServicesTypes } from './selectors';
import { getIsBookedOrConfirmed, getOrderId, getOrder } from '../order/selectors';
import { ApolloError, FetchResult } from 'apollo-boost';
import { fillOrder } from '../order/actions';
import { setOrderPrice } from '../orderPrice/actions';
import { setLoadingServicesParams, clearLoadingServicesParams } from '../loadingServicesParams/actions';
import { setServicesIsSaving, setServicesSaved } from '../servicesIsSaving/actions';
import { getEveryPortSelectedServicesArray } from '../everyPortServices/selectors';
import { SET_EVERY_PORT_ACTION } from '../everyPortServices/actions';
import {
	CheckoutOrderFragment,
	OrderQuery as OrderQueryResponse,
	OrderQueryVariables,
	OrderDocument,
	OrderPriceQueryQuery,
	OrderPriceQueryQueryVariables,
	SaveOrderServicesMutation,
	SaveOrderServicesMutationVariables,
	TravellerSelectedServices,
	SaveOrderServicesDocument,
	OrderPriceQueryDocument,
	SelectedNASProduct
} from '@websky/graphql';
import { LoadingServicesParamsState } from '../loadingServicesParams/reducers';
import { createExternalSagaActionChannel } from '../../../utils';

export const CHECKOUT_EXTERNAL_ACTION = 'CHECKOUT_EXTERNAL_ACTION';
const SHOW_ERROR_MODAL_ERRORS = ['FAILED TO CREATE AN ORDER'];

import { SET_EVERY_TRAVEL_CART } from '../everyTravel/actions';
import { EveryTravelState } from '../everyTravel/reducers';
import { getEveryTravelService } from '../everyTravel/selectors';
import { setErrorMessage } from '../errorMessage/actions';

function priceBreakdownRequest(
	services: TravellerSelectedServices[],
	everyPortServices: SelectedNASProduct[],
	everyTravel: EveryTravelState,
	orderId: string
) {
	const client = createClient();

	return client.mutate<OrderPriceQueryQuery, OrderPriceQueryQueryVariables>({
		mutation: OrderPriceQueryDocument,
		variables: {
			params: {
				id: orderId,
				services: services,
				nas: {
					services: [...everyPortServices]
				},
				everyTravel: {
					cartId: everyTravel.cartId?.toString() ?? null
				}
			}
		}
	});
}

function saveSelectedServicesRequest(
	services: TravellerSelectedServices[],
	everyPortServices: SelectedNASProduct[],
	everyTravel: EveryTravelState,
	orderId: string,
	context?: { [key: string]: string }
) {
	const client = createClient();

	return client.mutate<SaveOrderServicesMutation, SaveOrderServicesMutationVariables>({
		mutation: SaveOrderServicesDocument,
		variables: {
			params: {
				id: orderId,
				services: services,
				nas: {
					services: [...everyPortServices]
				},
				everyTravel: {
					cartId: everyTravel.cartId?.toString() ?? null
				}
			}
		},
		context
	});
}

function actualOrderRequest(orderId: string): Promise<CheckoutOrderFragment | null> {
	const client = createClient();

	return new Promise(resolve => {
		client
			.mutate<OrderQueryResponse, OrderQueryVariables>({
				mutation: OrderDocument,
				variables: {
					id: orderId
				}
			})
			.then(response => {
				if (response?.data?.Order) {
					resolve(response.data.Order);
				} else {
					resolve(null);
				}
			})
			.catch(() => {
				resolve(null);
			});
	});
}

function* getActualOrder(orderId: string) {
	const actualOrder: CheckoutOrderFragment = yield call(actualOrderRequest, orderId);
	if (actualOrder) {
		return actualOrder;
	}

	const previousOrderState: CheckoutOrderFragment = yield select(getOrder);

	return previousOrderState;
}

function* saveServiceWorker(action: ActionMeta<ServicePayload | ServicePayload[], ServiceMeta>) {
	const selectedServices: TravellerSelectedServices[] = yield select(getSelectedServices),
		everyPortSelectedServices: SelectedNASProduct[] = yield select(getEveryPortSelectedServicesArray),
		everyTravelServices: EveryTravelState = yield select(getEveryTravelService),
		orderId: string = yield select(getOrderId),
		isBookedOrConfirmedOrder: boolean = yield select(getIsBookedOrConfirmed),
		selectedServicesTypes: LoadingServicesParamsState = yield select(getSelectedServicesTypes(action.payload));

	yield put(setServicesIsSaving());
	yield put(setLoadingServicesParams(selectedServicesTypes));

	try {
		if (!isBookedOrConfirmedOrder) {
			const orderPrice: FetchResult<OrderPriceQueryQuery> = yield call(
				priceBreakdownRequest,
				selectedServices,
				everyPortSelectedServices,
				everyTravelServices,
				orderId
			);

			if (!orderPrice.data?.OrderPrice || orderPrice.errors?.length) {
				throw new Error(orderPrice.errors[0]?.message);
			}

			yield put(setOrderPrice(orderPrice.data.OrderPrice));
		}

		const saveServices: FetchResult<SaveOrderServicesMutation> = yield call(
			saveSelectedServicesRequest,
			selectedServices,
			everyPortSelectedServices,
			everyTravelServices,
			orderId,
			action.meta
		);

		if (!saveServices.data?.SaveOrderServices || saveServices.errors?.length) {
			throw new Error(saveServices.errors[0]?.message);
		}

		yield put(fillOrder(saveServices.data.SaveOrderServices));
	} catch (error) {
		let errorMessage = 'Unknown error';

		if (error instanceof ApolloError || error instanceof Error) {
			errorMessage = error.message;
		}

		const actualOrder: CheckoutOrderFragment = yield call(getActualOrder, orderId);
		yield put(fillOrder(actualOrder));

		if (SHOW_ERROR_MODAL_ERRORS.some(err => errorMessage.includes(err))) {
			yield put(setErrorMessage(errorMessage));
		}
	}

	yield put(setServicesSaved());
	yield put(clearLoadingServicesParams());
}

export function* saveServicesSaga() {
	yield takeLatest(
		[
			ADD_SERVICE,
			REMOVE_SERVICE,
			SAVE_SERVICES,
			SET_SERVICE,
			DELETE_ALL_TYPES_ON_SEGMENT,
			SET_MULTIPLE_SELECTED_SERVICES,
			SET_EVERY_PORT_ACTION,
			SET_EVERY_TRAVEL_CART
		],
		saveServiceWorker
	);
}

function* externalSaga() {
	const channel: Channel<Action<any>> = yield createExternalSagaActionChannel<Action<any>>(CHECKOUT_EXTERNAL_ACTION);

	while (true) {
		const action: Action<any> = yield take(channel);

		yield put(action);
	}
}

export function* watchExternalActionsSaga() {
	yield externalSaga();
}
